import { config } from '../../config/environment';
import { DummyDataService } from '../../services/DummyDataService';
import { getAuthToken } from '../../services/authToken';
import { authedApiFetch } from '../../services/tokenRefresh';
import type { Challenge, ChallengesResponse } from './challengeTypes';

// Use environment-based configuration with fallback to REACT_APP_API_URL
export const RESTAURANT_BACKEND_HOST = config.restaurantBackendHost || process.env.REACT_APP_API_URL || 'api.bespokecast.ai';

// Get the full backend URL with correct protocol
export const getBackendUrl = (path: string = '') => {
  // Use HTTP for localhost (fake and dev), HTTPS for production
  const protocol = config.restaurantBackendHost.includes('localhost') ? 'http' : 'https';
  return `${protocol}://${config.restaurantBackendHost}${path}`;
};

// Helper function to make API calls with correct protocol
export const apiCall = async (path: string, options: RequestInit = {}) => {
  // In dev mode, use dummy data instead of making real API calls
  if (config.useDummyData) {
    console.log(`[DEV MODE] Using dummy data for: ${path}`);
    return DummyDataService.simulateApiCall(path, options);
  }

  // In dev/prod mode, make real API calls. feat-028: route through the shared
  // authed-fetch wrapper so the Cognito access token is kept fresh (proactive
  // refresh + 401-retry-once). It attaches/overrides the bearer with a fresh
  // token when a session exists, and leaves genuinely unauthed calls untouched.
  const url = getBackendUrl(path);
  return authedApiFetch(url, options);
};

const parseChallengesResponse = async (response: Response): Promise<Challenge[]> => {
  if (!response.ok) {
    throw new Error(`Failed to fetch challenges: ${response.status}`);
  }
  const body = (await response.json()) as ChallengesResponse;
  return Array.isArray(body?.challenges) ? body.challenges : [];
};

export const fetchChallengesForLocation = async (
  locationId: string,
  query: string = '',
  options: RequestInit = {},
): Promise<Challenge[]> => {
  const path = `/api/locations/${encodeURIComponent(locationId)}/challenges${query}`;
  // Per-call bearer attachment (not in shared apiCall) to keep blast radius narrow — feat-007.
  const token = getAuthToken();
  const headers = new Headers(options.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const response = await apiCall(path, { ...options, headers });
  return parseChallengesResponse(response);
};
